import { useState, useEffect, useCallback, useRef } from "react";
import { Download, Search } from "lucide-react";
import { DataTable, cycleSortDir, Column } from "../components/DataTable";
import { useToastStore } from "../../stores/toastStore";
import { useAuthStore } from "../../stores/authStore";
import { Dropdown } from "../components/Dropdown";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import {
  postPagedSearchAbsolute,
  type AuditLogItem,
} from "../../api/frameworksApi";
import { TableToolbar } from "../components/TableToolbar";

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST /audit-log/feed/{page}/{size}. Sorting is fully server-side; a key
 * with no entry here is forwarded as-is.
 */
const AUDIT_SORT_COLUMNS: Record<string, string> = {
  displayName: "displayName",
  userId: "userId",
  module: "module",
  actionType: "actionType",
  changeSourceDetail: "changeSourceDetail",
  createdAt: "createdAt",
};

const MODULE_OPTIONS = [
  { value: "", label: "All Modules" },
  { value: "cmdb", label: "CMDB" },
  { value: "assessment_plan", label: "Assessment Plan" },
  { value: "user", label: "User" },
  { value: "role_mapping", label: "Role Mapping" },
  { value: "ssp", label: "SSP" },
  { value: "poam", label: "POAM" },
  { value: "assessment_execution", label: "Assessment Execution" },
  { value: "assessment_review", label: "Assessment Review" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

// Known acronyms that should stay fully uppercase when a raw enum is formatted.
const LABEL_ACRONYMS = new Set([
  "cmdb",
  "ssp",
  "poam",
  "api",
  "mfa",
  "sso",
  "ldap",
  "rbac",
]);

// Turn a raw snake_case enum (e.g. "role_mapping") into a readable label
// ("Role Mapping"), keeping known acronyms uppercase ("ssp" -> "SSP").
const formatLabel = (value: string): string => {
  return (value ?? "")
    .split("_")
    .map((word) =>
      LABEL_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

// "component-definition" / "assessment_plan" → "componentDefinition" / "assessmentPlan"
const toCamelCase = (value: string): string =>
  value
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");

const getActionColor = (action: string): string => {
  const colors: Record<string, string> = {
    create:
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    update: "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400",
    delete: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400",
    export:
      "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400",
    import:
      "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
    approve:
      "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400",
    submit:
      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400",
  };
  return (
    colors[action] ||
    "bg-gray-100 dark:bg-gray-500/15 text-gray-700 dark:text-gray-400"
  );
};

const MODULE_PALETTE: string[] = [
  "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
  "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400",
  "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400",
  "bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400",
  "bg-cyan-100 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-400",
  "bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400",
  "bg-teal-100 dark:bg-teal-500/15 text-teal-700 dark:text-teal-400",
];
const getModuleColor = (module: string): string => {
  if (!module)
    return "bg-gray-100 dark:bg-gray-500/15 text-gray-700 dark:text-gray-400";
  let hash = 0;
  for (let i = 0; i < module.length; i++)
    hash = (hash * 31 + module.charCodeAt(i)) >>> 0;
  return MODULE_PALETTE[hash % MODULE_PALETTE.length];
};

interface AuditLogResponse {
  status: "success" | "error";
  data?: {
    content: AuditLogItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
    };
  };
  error?: {
    code: string;
    message: string;
    details: string | null;
  };
}

export function AuditLogs() {
  const toastStore = useToastStore();
  const { getToken } = useAuthStore();

  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [totalItems, setTotalItems] = useState(0);
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [auditKeyword, setAuditKeyword] = useState("");

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the backend's default order),
  // which sends sortByColumn: "" / sortingOrder: "" the way it did on first load.
  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setCurrentPage(0); // any order change restarts at the first page
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
  };

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitAuditSearch = (value: string) => {
    const next = value.trim();
    if (next === auditKeyword) return;
    setAuditKeyword(next);
    setCurrentPage(0);
  };

  // The whole trail is searched, filtered, sorted and paged by the backend,
  // so there is no full-trail cache to keep any more — searching used to
  // download every page in parallel and filter client-side.
  // The spinner is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchAuditLogs = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    try {
      const res = await postPagedSearchAbsolute<AuditLogItem>(
        `${window.BE_URL || ""}/api/v1/audit-log/feed`,
        currentPage,
        pageSize,
        {
          keyword: auditKeyword.trim(),
          // Unsorted (the initial state) sends both sort fields as empty.
          sortByColumn: sortKey ? (AUDIT_SORT_COLUMNS[sortKey] ?? sortKey) : "",
          sortingOrder: sortKey ? sortDir : "",
          filters: {
            module: selectedModule || null,
            userId: selectedUserId.trim() || null,
            fromDate: fromDate || null,
            toDate: toDate || null,
          },
        },
      );
      setAuditLogs(res.items);
      setTotalItems(res.totalItems);
      // The current page can fall off the end of the result set (a larger page
      // size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && currentPage > 0 && res.totalItems > 0) {
        setCurrentPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (error: any) {
      console.error("Failed to fetch audit logs:", error);
      toastStore.showError(
        "Error",
        error.message || "Failed to load audit logs",
      );
      setAuditLogs([]);
      setTotalItems(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    selectedModule,
    selectedUserId,
    fromDate,
    toDate,
    auditKeyword,
    sortKey,
    sortDir,
    toastStore,
  ]);
  // Refetch whenever the page, page size, or any filter/search changes.
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  /** Serial number continues across pages (page 2 with 10/page starts at 11). */
  const auditRowSerial = (index: number) => currentPage * pageSize + index + 1;

  const columns: Column<AuditLogItem>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => <span className="d text-sm">{auditRowSerial(i)}</span>,
    },
    {
      key: "displayName",
      header: "Username",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (row) => (
        <span className="text-sm">
          {row.displayName || <span className="text-xs d italic">System</span>}
        </span>
      ),
    },
    {
      key: "module",
      header: "Module",
      align: "center",
      headerAlign: "center",
      sortable: true,
      render: (row) => (
        <span
          className={`inline-flex items-center justify-center whitespace-nowrap min-w-[150px] text-xs font-medium px-2 py-0.5 rounded ${getModuleColor(row.module)}`}
        >
          {formatLabel(row.module)}
        </span>
      ),
    },
    {
      key: "actionType",
      header: "Action",
      align: "center",
      sortable: true,
      render: (row) => (
        <span
          className={`inline-flex items-center justify-center whitespace-nowrap min-w-[88px] text-xs font-medium px-2 py-0.5 rounded ${getActionColor(row.actionType)}`}
        >
          {formatLabel(row.actionType)}
        </span>
      ),
    },
    {
      key: "changeSourceDetail",
      header: "Detail",
      align: "left",
      headerAlign: "center",
      sortable: true,
      ellipsis: 2,
      tooltip: (row) => row.changeSourceDetail,
      render: (row) => (
        <span className="text-sm">
          {row.changeSourceDetail}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      align: "left",
      headerAlign: "center",
      sortable: true,
      render: (row) => (
        <span className="text-sm font-medium">
          {formatTimestamp(row.createdAt)}
        </span>
      ),
    },
  ];

  const sortedLogs = sortKey
    ? [...auditLogs].sort((a, b) => {
        if (sortKey === "createdAt") {
          const av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortDir === "asc" ? av - bv : bv - av;
        }
        const av = String(
          (a as unknown as Record<string, unknown>)[sortKey] ?? "",
        ).toLowerCase();
        const bv = String(
          (b as unknown as Record<string, unknown>)[sortKey] ?? "",
        ).toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      })
    : auditLogs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Audit Log</h1>
          <p className="text-sm d mt-0.5">
            Complete audit trail of all platform activity
          </p>
        </div>
        {/* <button className="self-start sm:self-auto flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors flex-shrink-0">
          <Download className="size-4" />
          Export
        </button> */}
      </div>

      {/* Filters */}
      {/* <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">Module</label>
            <Dropdown
              size="sm"
              variant="form"
              value={selectedModule}
              onChange={(v) => handleFilterChange(() => setSelectedModule(v))}
              options={MODULE_OPTIONS}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">User ID</label>
            <input
              type="text"
              placeholder="Enter user ID"
              value={selectedUserId}
              onChange={(e) =>
                handleFilterChange(() => setSelectedUserId(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                handleFilterChange(() => setFromDate(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium d">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                handleFilterChange(() => setToDate(e.target.value))
              }
              className="px-3 py-1.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-foreground"
            />
          </div>
        </div>

        {(selectedModule || selectedUserId || fromDate || toDate) && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSelectedModule("");
                setSelectedUserId("");
                setFromDate("");
                setToDate("");
                setCurrentPage(0);
              }}
              className="text-xs px-3 py-1 rounded-md border border-border hover:bg-accent transition-colors text-d"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div> */}

      {/* Search + Rows per page */}
      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          className="flex-none w-full min-w-[220px] max-w-sm"
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            onSubmit: submitAuditSearch,
            placeholder: "Search audit logs",
            ariaLabel: "Search audit logs",
          }}
        />

        <div className="flex items-center gap-2 ml-auto">
          <RowsPerCardSelect
            value={pageSize}
            onChange={(n) => {
              setPageSize(n);
              setCurrentPage(0);
            }}
            options={PAGE_SIZE_OPTIONS}
            label="Rows per page"
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={sortedLogs}
        loading={loading}
        rowKey={(r) => r.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        emptyText={
          searchQuery.trim() ||
          selectedModule ||
          selectedUserId ||
          fromDate ||
          toDate
            ? "No audit logs match your filters."
            : "No audit logs found."
        }
        page={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={(p) => setCurrentPage(Math.max(0, p))}
        itemLabel="audit logs"
      />
    </div>
  );
}
